import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  scheduleApi,
  type Schedule,
  type CreateScheduleData,
  type UpdateScheduleData,
} from '../../api/schedule';
import { showsApi, type Show } from '../../api/shows';

const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

interface ScheduleFormProps {
  initialData?: Schedule | null;
  onSuccess?: () => void;
  onCreated?: (scheduleId: string) => void;
  onCancel: () => void;
}

export default function ScheduleForm({
  initialData,
  onSuccess,
  onCreated,
  onCancel,
}: ScheduleFormProps) {
  const now = new Date();
  const currentTime = now.toTimeString().slice(0, 5);
  const endTime = `${(now.getHours() + 1)
    .toString()
    .padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shows, setShows] = useState<Show[]>([]);

  const [formData, setFormData] = useState({
    showId: '',
    date: new Date().toISOString().split('T')[0],
    startTime: currentTime,
    endTime,
    days: [] as string[],
    isRecurring: false,
    status: 'upcoming',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getDayOfWeek = (dateString: string): DayOfWeek => {
    const date = new Date(dateString);
    return DAYS_OF_WEEK[date.getDay()];
  };

  const extractTime = (dateTime: string | Date): string => {
    if (!dateTime) return '';
    if (typeof dateTime === 'string' && /^\d{2}:\d{2}$/.test(dateTime)) {
      return dateTime;
    }
    const d = new Date(dateTime);
    if (isNaN(d.getTime())) return '';
    return `${d.getHours().toString().padStart(2, '0')}:${d
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  useEffect(() => {
    const loadShows = async () => {
      try {
        const res = await showsApi.getShows({});
        setShows(res.success ? res.data.data : []);
      } catch {
        toast.error('Failed to load shows');
      }
    };

    loadShows();

    if (initialData) {
      setFormData({
        showId: initialData.show.id,
        date: initialData.date.split('T')[0],
        startTime: extractTime(initialData.startTime),
        endTime: extractTime(initialData.endTime),
        days: [...initialData.days],
        isRecurring: (initialData.days?.length || 0) > 0,
        status: initialData.status || 'upcoming',
      });
    } else {
      const today = new Date().toISOString().split('T')[0];
      setFormData((p) => ({
        ...p,
        date: today,
        days: [getDayOfWeek(today)],
      }));
    }
  }, [initialData]);


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };

      if (name === 'date' && !prev.isRecurring) {
        next.days = [getDayOfWeek(value)];
      }

      return next;
    });

    if (errors[name]) {
      setErrors((p) => ({ ...p, [name]: '' }));
    }
  };

  const toggleDay = (day: DayOfWeek) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  const validateForm = () => {
    const e: Record<string, string> = {};

    if (!formData.showId) e.showId = 'Please select a show';
    if (!formData.startTime) e.startTime = 'Start time is required';
    if (!formData.endTime) e.endTime = 'End time is required';

    if (!formData.isRecurring && !formData.date) {
      e.date = 'Date is required';
    }

    if (formData.isRecurring && formData.days.length === 0) {
      e.days = 'Select at least one day';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const dateStr = formData.isRecurring
      ? new Date().toISOString().split('T')[0]
      : formData.date;

    const toISO = (t: string) =>
      new Date(`${dateStr}T${t.length === 5 ? `${t}:00` : t}`).toISOString();

    const payload: CreateScheduleData = {
      showId: formData.showId,
      date: toISO(formData.startTime),
      startTime: toISO(formData.startTime),
      endTime: toISO(formData.endTime),
      days: formData.isRecurring ? formData.days : [],
      status: formData.status,
    };

    try {
      setIsSubmitting(true);

      const res = initialData
        ? await scheduleApi.updateSchedule({
            id: initialData.id,
            ...payload,
          } as UpdateScheduleData)
        : await scheduleApi.createSchedule(payload);

      if (!res.success) {
        toast.error(res.message);
        setIsSubmitting(false);
        return;
      }

      toast.success(res.message);

      /** 🔑 NEW BEHAVIOR */
      if (!initialData && onCreated) {
        onCreated(res.data.id);
        return;
      }

      onSuccess?.();
    } catch {
      toast.error('Failed to save schedule');
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* Status Field */}
        {initialData?.id &&
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={handleChange}
              name="status"
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
            >
              <option value="scheduled">Scheduled</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        }

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Show</label>
          <select
            value={formData.showId}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, showId: e.target.value }));
              if (errors.showId) {
                setErrors(prev => ({ ...prev, showId: '' }));
              }
            }}
            disabled={isSubmitting}
            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"
          >
            <option value="">Select a show</option>
            {shows.map((show) => (
              <option key={show.id} value={show.id}>
                {show.name}
              </option>
            ))}
          </select>
          {errors.showId && <p className="text-red-500 text-xs mt-1">{errors.showId}</p>}
        </div>

        <div className="flex items-center gap-4 mb-4 p-3 bg-black/10 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="singleEvent"
                name="scheduleType"
                checked={!formData.isRecurring}
                onChange={() => setFormData(prev => ({
                  ...prev,
                  isRecurring: false,
                  days: prev.days.length ? [prev.days[0]] : [getDayOfWeek(prev.date || new Date().toISOString().split('T')[0])]
                }))}
                className="w-4 h-4 text-cyan-500 focus:ring-cyan-500"
              />
              <label htmlFor="singleEvent" className="text-sm font-medium text-gray-300">
                One-time Event
              </label>
            </div>
            <p className="text-xs text-gray-400 ml-6 mt-1">Schedule for a specific date</p>
          </div>

          <div className="h-8 w-px bg-white/10"></div>

          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                type="radio"
                id="recurringEvent"
                name="scheduleType"
                checked={formData.isRecurring}
                onChange={() => setFormData(prev => ({
                  ...prev,
                  isRecurring: true,
                  date: ''
                }))}
                className="w-4 h-4 text-cyan-500 focus:ring-cyan-500"
              />
              <label htmlFor="recurringEvent" className="text-sm font-medium text-gray-300">
                Recurring Event
              </label>
            </div>
            <p className="text-xs text-gray-400 ml-6 mt-1">Repeat on selected days</p>
          </div>
        </div>

        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-300 mb-1">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            disabled={isSubmitting}
            // min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"

          />
          {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
        </div>

        {formData.isRecurring && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Repeat On <span className="text-red-500">*</span>
              <p className="text-xs text-gray-400 mt-1">Select days for the recurring schedule</p>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <div
                  key={day}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${formData.days.includes(day)
                    ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                    : 'bg-black/20 border-white/10 text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                  onClick={() => toggleDay(day)}
                >
                  <div className="w-4 h-4 rounded border border-white/20 flex items-center justify-center">
                    {formData.days.includes(day) && (
                      <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                    )}
                  </div>
                  <span className="text-sm font-medium capitalize">
                    {day}
                  </span>
                </div>
              ))}
            </div>
            {errors.days && <p className="text-red-500 text-xs mt-1">{errors.days}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-300 mb-1">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              id="startTime"
              name="startTime"
              value={formData.startTime}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"

            />
            {errors.startTime && <p className="text-red-500 text-xs mt-1">{errors.startTime}</p>}
          </div>

          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-300 mb-1">
              End Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              id="endTime"
              name="endTime"
              value={formData.endTime}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-sm text-white placeholder:text-slate-500 transition-all"

            />
            {errors.endTime && <p className="text-red-500 text-xs mt-1">{errors.endTime}</p>}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 border border-white/10 rounded-xl bg-transparent hover:bg-white/5 text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {initialData ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            initialData ? 'Update Schedule' : 'Create Schedule'
          )}
        </button>
      </div>
    </form>
  );
}
